import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Footer "System status ●" pill.
 * - Polls /api/health every 60s.
 * - Green dot = backend + DB ok. Amber = degraded. Red = down.
 * - Links to the UptimeRobot public status page if VITE/CRA env var is set.
 * - Designed for the dark navy footer — adjust `mode="dark"` to invert text colour.
 */
export default function SystemStatusPill({ mode = "dark" }) {
  const [status, setStatus] = useState("checking"); // checking | ok | degraded | down
  const [lastChecked, setLastChecked] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const STATUS_PAGE_URL = process.env.REACT_APP_STATUS_PAGE_URL || "";

    const ping = async () => {
      try {
        const r = await api.get("/health", { timeout: 8000 });
        if (cancelled) return;
        const ok = r?.data?.status === "ok" && r?.data?.db === "ok";
        setStatus(ok ? "ok" : "degraded");
      } catch {
        if (cancelled) return;
        setStatus("down");
      } finally {
        if (!cancelled) setLastChecked(Date.now());
      }
    };

    ping();
    const id = setInterval(ping, 60000); // poll every 60s
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // STATUS_PAGE_URL is captured by closure; we only re-init when component re-mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const STATUS_PAGE_URL = process.env.REACT_APP_STATUS_PAGE_URL || "";

  const palette = {
    ok: { dot: "bg-emerald-400", label: "All systems operational" },
    degraded: { dot: "bg-amber-400", label: "Degraded performance" },
    down: { dot: "bg-rose-500", label: "Service disruption" },
    checking: { dot: "bg-slate-400 animate-pulse", label: "Checking status…" },
  }[status];

  const textCls = mode === "dark" ? "text-white/60 hover:text-[#C5A059]" : "text-slate-500 hover:text-[#0A192F]";

  const inner = (
    <span data-testid="system-status-pill" className={`inline-flex items-center gap-2 text-xs ${textCls} transition`}>
      <span className={`inline-block w-2 h-2 rounded-full ${palette.dot}`} aria-hidden="true" />
      <span data-testid="system-status-label">{palette.label}</span>
    </span>
  );

  if (!STATUS_PAGE_URL) {
    return (
      <span
        title={
          lastChecked
            ? `Last checked ${new Date(lastChecked).toLocaleTimeString()}`
            : "Live health check"
        }
      >
        {inner}
      </span>
    );
  }
  return (
    <a
      href={STATUS_PAGE_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="system-status-link"
      title={
        lastChecked
          ? `Last checked ${new Date(lastChecked).toLocaleTimeString()} · View full status page`
          : "View full status page"
      }
    >
      {inner}
    </a>
  );
}
